import { Suspense, lazy, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import type { ModalImport } from './types';

const MODAL_PARAM = 'modal';
const PAYLOAD_PARAM = 'payload';

/**
 * Creates a type-safe modal system with URL-based state management.
 *
 * @param registry - Object mapping modal names to lazy import functions
 * @returns `useModal` hook and `ModalManager` component configured for the registry
 *
 * @example
 * ```tsx
 * // In modals.ts
 * export type ModalPayloads = {
 *   CONFIRM_DELETE: { id: string };
 *   SETTINGS: undefined;
 * };
 *
 * export const { useModal, ModalManager } = createModalSystem<ModalPayloads>({
 *   CONFIRM_DELETE: () => import('components/ConfirmDeleteModal'),
 *   SETTINGS: () => import('components/SettingsModal'),
 * });
 *
 * // In components
 * const { open, close, isOpen } = useModal();
 * open('CONFIRM_DELETE', { id: '123' });
 * ```
 */
export function createModalSystem<TPayloads extends Record<string, unknown>>(
  registry: Record<keyof TPayloads, ModalImport>,
) {
  type ModalName = keyof TPayloads & string;

  function isValidModalName(name: string | null): name is ModalName {
    return name !== null && name in registry;
  }

  /**
   * Hook for managing URL-based modals.
   *
   * Modals are stored in URL search params, making them:
   * - Shareable via URL
   * - Persist across page refresh
   * - Work with browser back/forward navigation
   */
  function useModal() {
    const [searchParams, setSearchParams] = useSearchParams();

    const currentModal = useMemo(() => {
      const modal = searchParams.get(MODAL_PARAM);
      return isValidModalName(modal) ? modal : null;
    }, [searchParams]);

    const payload = useMemo(() => {
      const encoded = searchParams.get(PAYLOAD_PARAM);
      if (!encoded) return undefined;
      try {
        return JSON.parse(decodeURIComponent(encoded));
      } catch {
        return undefined;
      }
    }, [searchParams]);

    /**
     * Open a modal by name with an optional type-safe payload.
     */
    const open = useCallback(
      <T extends ModalName>(
        name: T,
        ...args: TPayloads[T] extends undefined ? [] : [payload: TPayloads[T]]
      ) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set(MODAL_PARAM, name);

        const modalPayload = args[0];
        if (modalPayload !== undefined) {
          newParams.set(
            PAYLOAD_PARAM,
            encodeURIComponent(JSON.stringify(modalPayload)),
          );
        } else {
          newParams.delete(PAYLOAD_PARAM);
        }

        setSearchParams(newParams, { replace: true, preventScrollReset: true });
      },
      [searchParams, setSearchParams],
    );

    /**
     * Close the currently open modal.
     */
    const close = useCallback(() => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(MODAL_PARAM);
      newParams.delete(PAYLOAD_PARAM);
      setSearchParams(newParams, { replace: true, preventScrollReset: true });
    }, [searchParams, setSearchParams]);

    /**
     * Check if a modal is open.
     * @param name - Optional modal name. If not provided, returns true if any modal is open.
     */
    const isOpen = useCallback(
      (name?: ModalName): boolean => {
        if (name === undefined) return currentModal !== null;
        return currentModal === name;
      },
      [currentModal],
    );

    return {
      /** The currently open modal name, or null if no modal is open */
      currentModal,
      /** The payload of the currently open modal */
      payload,
      /** Open a modal */
      open,
      /** Close the current modal */
      close,
      /** Check if a modal (or any modal) is open */
      isOpen,
    };
  }

  /**
   * Renders the currently active modal.
   * Add this component once at the root of your app inside BrowserRouter.
   */
  function ModalManager() {
    const { currentModal, payload, close } = useModal();

    const ModalComponent = useMemo(() => {
      if (!currentModal || !isValidModalName(currentModal)) return null;
      return lazy(registry[currentModal]);
    }, [currentModal]);

    if (!ModalComponent) return null;

    return (
      <Suspense fallback={null}>
        <ModalComponent close={close} payload={payload} />
      </Suspense>
    );
  }

  return { useModal, ModalManager };
}
